import { SavedViewPicker } from "../savedViews/SavedViewPicker";
import {
  BUILT_IN_SUMMARY_VIEW_KEYS,
  BUILT_IN_SUMMARY_VIEWS
} from "./builtInSummaryViews";
import { describeSummaryView } from "./describeSummaryView";
import {
  ALL_SUMMARY_VIEW_SECTIONS,
  parseSummaryViewPayload,
  type SummaryViewSections
} from "./summaryViewPayload";
import type { SummaryViewState } from "./useSummaryViewState";

/** What a save can carry, plus the default flag. */
const SAVE_TOGGLES = [
  { key: "dataShape", label: "Plan by, aggregation" },
  { key: "columns", label: "KPI columns" },
  { key: "dims", label: "Table and row dimension" },
  { key: "isDefault", label: "Set as default view" }
] as const;
const SECTION_KEYS = [
  "dataShape",
  "columns",
  "dims"
] as const satisfies readonly (keyof SummaryViewSections)[];
const SAVE_DEFAULTS = { ...ALL_SUMMARY_VIEW_SECTIONS, isDefault: false };
const BUILT_INS = BUILT_IN_SUMMARY_VIEW_KEYS.map((key) => ({
  key,
  description: describeSummaryView(BUILT_IN_SUMMARY_VIEWS[key])
}));

interface SummaryViewSelectorProps {
  /** The page's Views layer (``useSummaryViewState``). */
  views: SummaryViewState;
  /** Sizing wrapper around the trigger. */
  slotClassName?: string;
}

/**
 * The control that names the summary setup: the Compact built-in and the
 * user's own views in one list. It replaces the KPI-category tabs and the
 * OPT toggle — which columns show is a view's to say, refined from the
 * column menu.
 */
export function SummaryViewSelector({
  views,
  slotClassName
}: SummaryViewSelectorProps) {
  return (
    <SavedViewPicker
      label="View"
      slotClassName={slotClassName}
      placeholder="Custom"
      builtIns={BUILT_INS}
      views={views.views}
      describe={(view) => {
        const payload = parseSummaryViewPayload(view.control_state);
        return payload
          ? describeSummaryView(payload) || "Nothing"
          : "Unreadable";
      }}
      activeView={views.activeView}
      activeSavedView={views.activeSavedView}
      isDirty={views.isDirty}
      onPickBuiltIn={views.applyBuiltIn}
      onPickSaved={views.applySavedView}
      writes={views}
      saveToggles={SAVE_TOGGLES}
      saveDefaults={SAVE_DEFAULTS}
      requireOneOf={SECTION_KEYS}
      onSave={(name, { isDefault, ...sections }) =>
        views.saveCurrentView(name, sections, isDefault)
      }
      onSaveOpenChange={views.setSaveOpen}
    />
  );
}
